import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Layers as LayersIcon,
  DragIndicator as DragIcon,
  Close as CloseIcon,
  InsertDriveFileOutlined as FileIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnsIcon,
  DensitySmall as CompactIcon,
  ArrowUpward as SortAscIcon,
  ArrowDownward as SortDescIcon,
  UnfoldMore as SortNeutralIcon,
  FolderOpen as FolderOpenIcon,
  Save as SaveIcon,
  KeyboardArrowUp as ChevronUpIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowLeft as ChevronLeftIcon,
  KeyboardArrowRight as ChevronRightIcon,
  VisibilityOff as HideUsedIcon,
  Inventory2 as HideArchivedIcon,
  Email as EmailIcon,
  InfoOutlined as InfoIcon,
  AutoFixHigh as CreateIcon,
  CheckCircleOutline as ReviewedIcon,
  Done as CheckIcon,
  AttachFile as AttachIcon,
} from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { sanitizeHtml } from "common/html";
import Button from "components/common/Button";
import SubmissionReviewDialog from "components/common/SubmissionReviewDialog";
import TriageResultDialog, {
  STALE_REPORT_HINT,
} from "components/common/TriageResultDialog";
import {
  agentSearchModesForRules,
  ruleDescriptionsByIngestionKey,
  FIELD_LABELS,
  sourcingByIngestionKey,
  TRIAGE_REVIEW_SECTIONS,
} from "components/common/submissionReviewFields";
import Dashboard from "components/layout/Dashboard";
import DashboardContent from "components/layout/DashboardContent";
import AttachFilesDialog from "components/triagePipeline/AttachFilesDialog";
import TriageRulesetsConfig from "components/triagePipeline/TriageRulesetsConfig";
import {
  CARRY_FORWARD_FIELDS,
  isInScope,
  LAYER_FIELDS,
} from "components/triagePipeline/triageDefinitions";
import { matchFileByName } from "fileUtils";
import {
  SUBMISSION_TRIAGE,
  AI_CARRY_FORWARD_PAGE,
  AI_CARRY_FORWARD_CONFIDENCE,
  AI_CARRY_FORWARD_EDIT,
  AI_CARRY_FORWARD_AGENT_SEARCH,
  AI_CARRY_FORWARD_ALL_DOCUMENTS,
} from "flags";
import { useSnackbar } from "notistack";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  useGetTriagePipelineQuery,
  useArchiveTriageResultMutation,
  useMarkTriageReviewedMutation,
  useGetTriageResultQuery,
  useRerunSubmissionTriageMutation,
  useDetachIngestionFilesMutation,
  useIngestionFileUrlMutation,
  useMarkFileAsUsedMutation,
  useAgentSearchIngestionFieldsMutation,
} from "services/aiAgents";
import { claimsApi } from "services/claimsService";
import { filesApi } from "services/filesService";
import {
  useRetrievePreferencesQuery,
  useUpdatePreferencesMutation,
} from "services/userService";
import * as attachmentsActions from "store/actions/input/attachments/attachmentsActions";
import * as programActions from "store/actions/input/program/programActions";
import * as persistenceActions from "store/actions/persistence/persistenceActions";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import { selectCurrencyCodes } from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import {
  buildFieldUsageByFile,
  clearFieldUsage,
  recordFieldSources,
  recordFieldValue,
  recordWebResearch,
  recordSuggestionSelection,
} from "utils/aiSuggestionUsageTracker";
import {
  describeEnumIssues,
  isEnumBackedField,
  resolveEnumField,
  resolveEnumFields,
} from "utils/enumFieldResolution";
import { downloadIngestionSource } from "utils/ingestionFiles";

// ─── Column definitions ───────────────────────────────────────────────────────

// camelCase to match the aliases UserPreferences serialises with (humps.camelize);
// the snake_case keys these used to be were silently dropped on save.
const PREFS_KEY_COLUMNS = "triagePipelineColumns";
const PREFS_KEY_COMPACT = "triagePipelineCompact";
const PREFS_KEY_STATS_COLLAPSED = "triagePipelineStatsCollapsed";

// Row metrics for the two table densities. Compact roughly halves row height by
// tightening cell padding rather than dropping any data.
const DENSITY = {
  comfortable: {
    cellPadY: "10px",
    headPadY: "6px",
    nameSize: "13px",
    metaSize: 11,
    metaGap: 0.2,
  },
  compact: {
    cellPadY: "3px",
    headPadY: "2px",
    nameSize: "12px",
    metaSize: 10,
    metaGap: 0,
  },
};

// Default column widths. A flat 160 for everything pushed the table past the viewport and
// gave a two-character score the same room as an insured's name. Read by both the width
// resolver and the resize handler, so a change cannot land in one and not the other.
const DEFAULT_COL_WIDTHS = {
  file: 280,
  score: 100,
  weighted: 112,
  // Wide enough for "Out of scope" beside an overruled pill's initials avatar and
  // chevron, which is the longest this cell gets.
  status: 150,
  reviewed: 104,
  inception: 120,
  expiration: 120,
  received: 132,
  ruleset: 140,
  brokerage: 112,
  industry_class: 148,
};
const FALLBACK_COL_WIDTH = 160;
export const defaultColWidth = (key) =>
  DEFAULT_COL_WIDTHS[key] ?? FALLBACK_COL_WIDTH;

export const DEFAULT_COLUMNS = [
  { key: "file", label: "Submission", pinned: true, visible: true },
  { key: "ruleset", label: "Ruleset", visible: true },
  { key: "received", label: "Received", visible: true },
  { key: "score", label: "Score", visible: true },
  { key: "weighted", label: "Weighted", visible: false },
  { key: "status", label: "Status", visible: true },
  { key: "insured", label: "Insured", visible: true },
  { key: "industry_class", label: "Industry", visible: true },
  { key: "inception", label: "Inception", visible: true },
  { key: "expiration", label: "Expiration", visible: false },
  { key: "folder", label: "Project", visible: false },
  { key: "brokerage", label: "Brokerage %", visible: false },
  { key: "maximum_possible_loss", label: "Max Loss", visible: false },
  { key: "naics", label: "NAICS", visible: false },
  { key: "transaction", label: "Transaction", visible: false },
  { key: "submission", label: "Used In", visible: false },
  { key: "reviewed", label: "Reviewed", visible: true },
];

// Merge saved column prefs with defaults (handles new columns added later)
const mergeColumns = (saved) => {
  if (!saved?.length) return DEFAULT_COLUMNS;
  const result = saved
    .map((s) => {
      const def = DEFAULT_COLUMNS.find((d) => d.key === s.key);
      if (!def) return null;
      return { ...def, visible: s.visible };
    })
    .filter(Boolean);
  DEFAULT_COLUMNS.forEach((def) => {
    if (!result.find((r) => r.key === def.key)) result.push(def);
  });
  return result;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toISOString().slice(0, 10);
};

const getIngestionValue = (field) => {
  if (!field) return null;
  const val = field.value;
  if (val == null) return null;
  if (typeof val === "object" && !Array.isArray(val)) {
    const { code, title } = val;
    if (code != null && title) return `${code} – ${title}`;
    if (code != null) return String(code);
    return null;
  }
  return String(val);
};

// ── Field mapping: backend ingestion_fields keys → program state keys ─────────

// The row's Pass/Fail once a reviewer's overrides are taken into account.
//
// It must NOT be re-derived as `passed === total`. Not every rule gates: a layer rule
// scores without gating, so an item the backend passed legitimately sits below its own
// total (a real one passes at 5/6). Counting rows made every overridden item with any
// non-gating failure read Fail in the table while the drawer read Pass — and because the
// early return above skips items with no overrides, saving even a no-op override flipped
// the row. Same defect the drawer had; it was fixed there and not here.
//
// The list payload carries only the counts, not the per-rule score_breakdown the drawer
// uses, so the row cannot tell a gating rule from a scoring-only one. It therefore trusts
// the backend's verdict and moves it only where counts alone are conclusive.
export const applyOverrides = (
  summary,
  overrides,
  threshold = null,
  verdictOverride = null
) => {
  // A ruleset with an in-scope threshold is classified by its score alone: no single rule
  // hard-stops the submission, overridden or not. Derived from the CURRENT threshold
  // rather than read from the report, which is what makes editing the threshold
  // reclassify the pipeline with no re-run. The score is the one this row displays, so
  // the verdict and the number beside it always agree.
  const byThreshold = isInScope(summary?.weighted_score, threshold);
  if (!summary) return summary;
  // A reviewer's own scope decision outranks the score, the threshold and the gating
  // rules, so the row reads the same as the drawer they set it in. Only the verdict is
  // theirs; the counts still describe the documents, their rule edits included, as the
  // drawer's do.
  const decided = verdictOverride
    ? {
        overall_outcome: verdictOverride.user_in_scope ? "pass" : "fail",
        verdict_overridden: true,
      }
    : null;
  if (!overrides?.length) {
    if (decided) return { ...summary, ...decided };
    return byThreshold === null
      ? summary
      : { ...summary, overall_outcome: byThreshold ? "pass" : "fail" };
  }
  const total = summary.total_count;
  let delta = 0;
  let reviewerFailedARule = false;
  for (const o of overrides) {
    const wasPass = o.ai_outcome === "pass";
    const isPass = o.user_outcome === "pass";
    if (wasPass && !isPass) delta -= 1;
    if (!wasPass && isPass) delta += 1;
    // A layer rule is evaluated in code and scores without gating, so failing one cannot
    // fail the item. Its row is not editable either, but the guard keeps this in step with
    // the drawer rather than relying on that.
    if (wasPass && !isPass && !LAYER_FIELDS.has(o.field_name)) {
      reviewerFailedARule = true;
    }
  }
  const effectivePassed = Math.max(
    0,
    Math.min(total, summary.passed_count + delta)
  );
  const outcome =
    byThreshold !== null
      ? // The threshold decides, so an overridden rule moves the count on display without
        // moving the verdict. The score this row carries is the report's own; the drawer,
        // which can recompute it from the breakdown, classifies on the recomputed one.
        byThreshold
        ? "pass"
        : "fail"
      : reviewerFailedARule
      ? // A rule the reviewer turned pass → fail cannot leave a passing item passing.
        "fail"
      : effectivePassed === total
      ? // Every rule passes, so the item passes under any gating rule.
        "pass"
      : // Otherwise the backend's verdict stands: it is the only party here that knows
        // which of the remaining failures actually gate.
        summary.overall_outcome ?? "fail";
  return {
    ...summary,
    passed_count: effectivePassed,
    overall_outcome: outcome,
    ...decided,
  };
};

// ─── Stats computed from real pipeline data ───────────────────────────────────

const computeStats = (effectiveItems) => {
  const now = new Date();

  // Build last-10-days bucket
  const days = Array.from({ length: 10 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (9 - i));
    return d.toISOString().slice(0, 10);
  });
  const byDay = Object.fromEntries(days.map((d) => [d, []]));
  effectiveItems.forEach((item) => {
    const day = item.triage_completed_at?.slice(0, 10);
    if (day && byDay[day] !== undefined) byDay[day].push(item);
  });
  const dayArrs = days.map((d) => byDay[d]);

  const nonArchived = effectiveItems.filter((i) => !i.triage_archived);

  // 1. Received (7d vs prior 7d)
  const cutoff7 = new Date(now);
  cutoff7.setDate(cutoff7.getDate() - 7);
  const cutoff14 = new Date(now);
  cutoff14.setDate(cutoff14.getDate() - 14);
  const r7 = effectiveItems.filter((i) => {
    const d = i.triage_completed_at ? new Date(i.triage_completed_at) : null;
    return d && d >= cutoff7;
  }).length;
  const r14 = effectiveItems.filter((i) => {
    const d = i.triage_completed_at ? new Date(i.triage_completed_at) : null;
    return d && d >= cutoff14 && d < cutoff7;
  }).length;
  const rDelta =
    r14 > 0
      ? `${r7 >= r14 ? "+" : ""}${Math.round(((r7 - r14) / r14) * 100)}%`
      : r7 > 0
      ? "new"
      : "—";
  const rDir = r7 >= r14 ? "up" : "down";

  // 2. Pass rate
  const passedCount = nonArchived.filter(
    (i) => i._effective.overall_outcome === "pass"
  ).length;
  const passRate =
    nonArchived.length > 0
      ? ((passedCount / nonArchived.length) * 100).toFixed(1)
      : "0.0";
  const passRateSpark = dayArrs.map((arr) => {
    if (!arr.length) return 0;
    const p = arr.filter((i) => i._effective.overall_outcome === "pass").length;
    return (p / arr.length) * 100;
  });

  // 3a. Avg WEIGHTED score, over the items that carry an informative one. Averaged over
  // the scores themselves, not re-derived from summed counts — a weighted score is already
  // a ratio, and summing the parts would weight big rulesets more heavily than small ones.
  const weightedItems = nonArchived.filter(
    (i) => i.weights_informative && typeof i.weighted_score === "number"
  );
  const avgWeighted =
    weightedItems.length > 0
      ? weightedItems.reduce((s, i) => s + i.weighted_score, 0) /
        weightedItems.length
      : null;

  // 3. Avg score
  const avgPassed =
    nonArchived.length > 0
      ? nonArchived.reduce((s, i) => s + i._effective.passed_count, 0) /
        nonArchived.length
      : 0;
  const avgTotal =
    nonArchived.length > 0
      ? nonArchived.reduce((s, i) => s + i._effective.total_count, 0) /
        nonArchived.length
      : 1;
  const avgScoreSpark = dayArrs.map((arr) => {
    if (!arr.length) return 0;
    return (
      arr.reduce(
        (s, i) =>
          s +
          (i._effective.total_count > 0
            ? i._effective.passed_count / i._effective.total_count
            : 0),
        0
      ) / arr.length
    );
  });

  // 4. Awaiting review (not archived, not used)
  const awaiting = nonArchived.filter((i) => !i.used_in_submission).length;
  const awaitingSpark = dayArrs.map(
    (arr) =>
      arr.filter((i) => !i.triage_archived && !i.used_in_submission).length
  );

  return [
    {
      label: "Received (7d)",
      value: r7,
      unit: "submissions",
      delta: rDelta,
      dir: rDir,
      data: dayArrs.map((a) => a.length),
    },
    {
      label: "Pass rate",
      value: passRate,
      unit: "%",
      delta: `${passedCount} / ${nonArchived.length}`,
      dir: "up",
      data: passRateSpark,
    },
    // Weighted where the rulesets say rules are not equal, the plain fraction otherwise.
    // Showing the unweighted average against weighted rulesets was the aggregate view
    // contradicting the whole premise of weights.
    avgWeighted != null
      ? {
          label: "Avg. weighted",
          value: avgWeighted.toFixed(1),
          unit: "%",
          delta: `${weightedItems.length} scored`,
          dir: avgWeighted >= 50 ? "up" : "down",
          data: avgScoreSpark,
        }
      : {
          label: "Avg. score",
          value: avgPassed.toFixed(1),
          unit: `/ ${Math.round(avgTotal) || 1}`,
          delta: "—",
          dir: avgPassed / Math.max(avgTotal, 1) >= 0.5 ? "up" : "down",
          data: avgScoreSpark,
        },
    {
      label: "Awaiting review",
      value: awaiting,
      unit: "open",
      delta: `${nonArchived.length} total`,
      dir: awaiting < nonArchived.length ? "down" : "up",
      data: awaitingSpark,
    },
  ];
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Sparkline = ({ data, color = "#15803D" }) => {
  // Ensure we have at least 2 non-identical points so the line is visible
  const safe = data?.length >= 2 ? data : [0, 0];
  const W = 120,
    H = 24;
  const max = Math.max(...safe),
    min = Math.min(...safe);
  const range = max - min || 1;
  const pts = safe
    .map((v, i) => {
      const x = (i / (safe.length - 1)) * W;
      const y = H - ((v - min) / range) * (H - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <Box
      component={"svg"}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio={"none"}
      sx={{ width: "100%", height: H, display: "block", mt: 0.5 }}
    >
      <polyline
        points={pts}
        fill={"none"}
        stroke={color}
        strokeWidth={"1.3"}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}
        opacity={"0.85"}
      />
    </Box>
  );
};

const StatCard = ({ label, value, unit, delta, dir, data }) => (
  <Paper
    variant={"outlined"}
    sx={{
      p: "14px 16px",
      borderRadius: 2,
      bgcolor: "background.paper",
      position: "relative",
    }}
  >
    {/* Delta — absolutely positioned top-right */}
    <Stack
      direction={"row"}
      alignItems={"center"}
      spacing={0.25}
      sx={{ position: "absolute", top: 14, right: 16 }}
    >
      {dir === "up" ? (
        <SortAscIcon sx={{ fontSize: "0.65rem", color: "success.main" }} />
      ) : (
        <SortDescIcon sx={{ fontSize: "0.65rem", color: "error.main" }} />
      )}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
          color: dir === "up" ? "success.main" : "error.main",
        }}
      >
        {delta}
      </Typography>
    </Stack>

    {/* Label */}
    <Stack
      direction={"row"}
      alignItems={"center"}
      spacing={0.75}
      sx={{ mb: 1.25 }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "text.disabled",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{ fontSize: "11.5px", fontWeight: 500, color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Stack>

    {/* Value + unit — baseline aligned */}
    <Stack direction={"row"} alignItems={"baseline"} spacing={1}>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{ fontSize: 12, fontWeight: 400, color: "text.disabled" }}
      >
        {unit}
      </Typography>
    </Stack>

    <Sparkline data={data} color={dir === "up" ? "#15803D" : "#57534E"} />
  </Paper>
);

// Status pill matching design tokens
// The submission's own verdict. "Fail" in red read as something being wrong with the
// software rather than with the fit of the risk, so it says out of scope, in grey: a
// submission this ruleset does not want to look at is an ordinary outcome, not an error.
// The per-rule rows keep pass and fail — there, a rule really did or did not match.
export const StatusPill = ({ outcome, overridden = false }) => {
  const inScope = outcome === "pass";
  return (
    <Box
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1,
        py: 0.15,
        borderRadius: "10px",
        fontSize: "11.5px",
        fontWeight: 500,
        border: overridden
          ? `1px dashed ${theme.palette.warning.main}`
          : "none",
        bgcolor: inScope
          ? alpha(theme.palette.success.main, 0.12)
          : alpha(theme.palette.text.primary, 0.07),
        color: inScope ? "success.dark" : "text.secondary",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 5,
          height: 5,
          borderRadius: "50%",
          bgcolor: inScope ? "success.main" : "text.disabled",
          flexShrink: 0,
        })}
      />
      {inScope ? "In scope" : "Out of scope"}
    </Box>
  );
};

// Tag chip for ruleset
const TagChip = ({ children }) => (
  <Box
    component={"span"}
    sx={(theme) => ({
      display: "inline-block",
      px: "7px",
      py: "2px",
      borderRadius: "4px",
      fontSize: "11.5px",
      fontWeight: 400,
      color: "text.secondary",
      bgcolor: alpha(theme.palette.text.primary, 0.04),
      border: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
      maxWidth: 180,
      overflow: "hidden",
      textOverflow: "ellipsis",
    })}
  >
    {children}
  </Box>
);

// Score bar matching design spec
const ScoreBar = ({ passed, total }) => {
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const barColor =
    pct >= 100 ? "success.main" : pct > 0 ? "warning.main" : "error.main";
  return (
    <Box sx={{ minWidth: 80 }}>
      <Typography
        sx={{ fontSize: 12, fontFamily: "monospace", fontWeight: 500, mb: 0.3 }}
      >
        {`${passed}/${total}`}
      </Typography>
      <Box
        sx={(theme) => ({
          height: 3,
          borderRadius: 2,
          bgcolor: theme.palette.divider,
          maxWidth: 52,
        })}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: 2,
            bgcolor: barColor,
            width: `${pct}%`,
            transition: "width 200ms",
          }}
        />
      </Box>
    </Box>
  );
};

// The weighted score as a bar, with a notch at the ruleset's in-scope threshold. The
// number alone does not say which side of the line a submission falls on, and that line
// is the verdict whenever a ruleset sets one.
export const WeightedBar = ({ score, threshold }) => {
  if (typeof score !== "number") {
    return (
      <Typography sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
        {"\u2014"}
      </Typography>
    );
  }
  const clears = threshold == null ? null : score >= threshold;
  const barColor =
    clears === null ? "info.main" : clears ? "success.main" : "error.main";
  return (
    <Tooltip
      title={
        threshold == null
          ? `${score}% weighted`
          : `${score}% weighted · in scope at ${threshold}%`
      }
      arrow
    >
      <Box sx={{ minWidth: 80 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 500,
            mb: 0.3,
          }}
        >
          {`${score}%`}
        </Typography>
        <Box
          sx={(theme) => ({
            position: "relative",
            height: 3,
            borderRadius: 2,
            bgcolor: theme.palette.divider,
            maxWidth: 52,
          })}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: 2,
              bgcolor: barColor,
              width: `${Math.max(0, Math.min(100, score))}%`,
              transition: "width 200ms",
            }}
          />
          {threshold != null && (
            <Box
              data-testid={"weighted-threshold-marker"}
              sx={(theme) => ({
                position: "absolute",
                top: -2,
                bottom: -2,
                left: `${Math.max(0, Math.min(100, threshold))}%`,
                width: 2,
                borderRadius: 1,
                bgcolor: theme.palette.text.primary,
              })}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

// ─── Filter modal ─────────────────────────────────────────────────────────────

const FilterModal = ({
  open,
  onClose,
  filters,
  onApply,
  availableRulesets,
  availableBrokers,
}) => {
  const [local, setLocal] = useState(filters);
  useEffect(() => {
    if (open) setLocal(filters);
  }, [open, filters]);

  const toggleArr = (key, val) => {
    setLocal((f) => {
      const arr = f[key] || [];
      return {
        ...f,
        [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val],
      };
    });
  };

  const apply = () => {
    onApply(local);
    onClose();
  };

  const clear = () =>
    setLocal({
      status: [],
      ruleset: [],
      broker: [],
      reviewed: null,
      dateFrom: "",
      dateTo: "",
    });

  const FilterGroup = ({ label, children }) => (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "text.secondary",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );

  const FilterChip = ({ active, onClick, children }) => (
    <Box
      component={"button"}
      onClick={onClick}
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        height: 26,
        px: 1.5,
        borderRadius: "14px",
        fontSize: 12,
        fontWeight: active ? 500 : 400,
        cursor: "pointer",
        border: `1px ${active ? "solid" : "dashed"} ${
          active ? theme.palette.primary.main : theme.palette.action.disabled
        }`,
        bgcolor: active
          ? alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        color: active ? "primary.main" : "text.secondary",
        transition: "all 100ms",
        "&:hover": {
          bgcolor: active
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.text.primary, 0.04),
        },
      })}
    >
      {children}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"sm"}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction={"row"}
          alignItems={"flex-start"}
          justifyContent={"space-between"}
        >
          <Box>
            <Typography variant={"h6"} fontWeight={600}>
              {"Filter submissions"}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"Combine filters — results update when you apply."}
            </Typography>
          </Box>
          <IconButton
            size={"small"}
            onClick={onClose}
            sx={{ color: "text.secondary", mt: -0.5 }}
          >
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        <Stack spacing={2.5}>
          <FilterGroup label={"Status"}>
            <Stack direction={"row"} spacing={1}>
              {["pass", "fail"].map((s) => (
                <FilterChip
                  key={s}
                  active={(local.status || []).includes(s)}
                  onClick={() => toggleArr("status", s)}
                >
                  <StatusPill outcome={s} />
                </FilterChip>
              ))}
            </Stack>
          </FilterGroup>

          <FilterGroup label={"Reviewed"}>
            <Stack direction={"row"} spacing={1}>
              {[
                { value: true, label: "Reviewed" },
                { value: false, label: "Not reviewed" },
              ].map(({ value, label }) => (
                <FilterChip
                  key={String(value)}
                  active={local.reviewed === value}
                  onClick={() =>
                    setLocal((f) => ({
                      ...f,
                      reviewed: f.reviewed === value ? null : value,
                    }))
                  }
                >
                  {label}
                </FilterChip>
              ))}
            </Stack>
          </FilterGroup>

          {availableRulesets.length > 0 && (
            <FilterGroup label={"Ruleset"}>
              <Stack direction={"row"} flexWrap={"wrap"} gap={0.75}>
                {availableRulesets.map((r) => (
                  <FilterChip
                    key={r}
                    active={(local.ruleset || []).includes(r)}
                    onClick={() => toggleArr("ruleset", r)}
                  >
                    {r}
                  </FilterChip>
                ))}
              </Stack>
            </FilterGroup>
          )}

          {availableBrokers.length > 0 && (
            <FilterGroup label={"Broker"}>
              <Stack direction={"row"} flexWrap={"wrap"} gap={0.75}>
                {availableBrokers.map((b) => (
                  <FilterChip
                    key={b}
                    active={(local.broker || []).includes(b)}
                    onClick={() => toggleArr("broker", b)}
                  >
                    {b}
                  </FilterChip>
                ))}
              </Stack>
            </FilterGroup>
          )}

          <FilterGroup label={"Received between"}>
            <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
              <OutlinedInput
                type={"date"}
                size={"small"}
                value={local.dateFrom || ""}
                onChange={(e) =>
                  setLocal({ ...local, dateFrom: e.target.value })
                }
                sx={{ maxWidth: 180, fontSize: "0.85rem" }}
              />
              <Typography variant={"caption"} color={"text.disabled"}>
                {"to"}
              </Typography>
              <OutlinedInput
                type={"date"}
                size={"small"}
                value={local.dateTo || ""}
                onChange={(e) => setLocal({ ...local, dateTo: e.target.value })}
                sx={{ maxWidth: 180, fontSize: "0.85rem" }}
              />
            </Stack>
          </FilterGroup>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <MuiButton
          size={"small"}
          onClick={clear}
          sx={{
            fontSize: "0.78rem",
            textTransform: "none",
            color: "text.secondary",
            p: 0,
            minWidth: 0,
            "&:hover": { bgcolor: "transparent", color: "text.primary" },
          }}
        >
          {"Clear all"}
        </MuiButton>
        <Stack direction={"row"} spacing={1}>
          <MuiButton
            size={"small"}
            variant={"outlined"}
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "rgba(0,0,0,0.23)",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "rgba(0,0,0,0.4)",
              },
            }}
          >
            {"Cancel"}
          </MuiButton>
          <Button
            size={"small"}
            color={"secondary"}
            sx={{ textTransform: "none" }}
            onClick={apply}
          >
            {"Apply filters"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

// ─── Column picker modal ──────────────────────────────────────────────────────

const ColumnPickerModal = ({ open, onClose, columns, onApply }) => {
  const [local, setLocal] = useState(columns);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (open) setLocal(columns);
  }, [open, columns]);

  const toggle = (key) => {
    setLocal((c) =>
      c.map((x) =>
        x.key === key && !x.pinned ? { ...x, visible: !x.visible } : x
      )
    );
  };

  const move = (i, dir) => {
    const arr = [...local];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLocal(arr);
  };

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    setDragOverIndex(i);
  };
  const handleDrop = (i) => {
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const arr = [...local];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(i, 0, moved);
    setLocal(arr);
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const apply = () => {
    onApply(local);
    onClose();
  };

  const reset = () => setLocal(DEFAULT_COLUMNS);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"xs"}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction={"row"}
          alignItems={"flex-start"}
          justifyContent={"space-between"}
        >
          <Box>
            <Typography variant={"h6"} fontWeight={600}>
              {"Columns"}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"Drag to reorder. Toggle visibility."}
            </Typography>
          </Box>
          <IconButton
            size={"small"}
            onClick={onClose}
            sx={{ color: "text.secondary", mt: -0.5 }}
          >
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5, pb: 0, px: 1.5 }}>
        <Stack>
          {local.map((col, i) => (
            <Stack
              key={col.key}
              direction={"row"}
              alignItems={"center"}
              spacing={0.75}
              draggable={!col.pinned}
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
              sx={(theme) => ({
                py: 0.65,
                px: 0.5,
                borderRadius: 1,
                borderTop:
                  dragOverIndex === i && dragIndex !== i
                    ? `2px solid ${theme.palette.primary.main}`
                    : "2px solid transparent",
                opacity: dragIndex === i ? 0.4 : 1,
                transition: "opacity 80ms",
                cursor: col.pinned ? "default" : "grab",
                "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.03) },
              })}
            >
              {/* Drag handle */}
              <DragIcon
                sx={{
                  fontSize: "1rem",
                  color: col.pinned ? "transparent" : "text.disabled",
                  flexShrink: 0,
                  opacity: 0.5,
                  "&:hover": { opacity: 1 },
                }}
              />

              {/* Checkbox — dark when checked */}
              <Box
                component={"button"}
                onClick={() => toggle(col.key)}
                disabled={col.pinned}
                sx={(theme) => ({
                  width: 16,
                  height: 16,
                  borderRadius: "4px",
                  border: `1.5px solid ${
                    col.visible
                      ? theme.palette.text.primary
                      : theme.palette.action.disabled
                  }`,
                  bgcolor: col.visible ? "text.primary" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: col.pinned ? "default" : "pointer",
                  flexShrink: 0,
                  transition: "all 80ms",
                  p: 0,
                })}
              >
                {col.visible && (
                  <CheckIcon
                    sx={{ fontSize: "0.7rem", color: "background.paper" }}
                  />
                )}
              </Box>

              <Typography
                variant={"body2"}
                flex={1}
                sx={{
                  fontSize: "13px",
                  fontWeight: col.pinned ? 500 : 400,
                  opacity: col.visible ? 1 : 0.45,
                }}
              >
                {col.label}
              </Typography>
              {col.pinned && (
                <Typography
                  variant={"caption"}
                  sx={{
                    fontSize: 10.5,
                    color: "text.disabled",
                    fontFamily: "monospace",
                  }}
                >
                  {"pinned"}
                </Typography>
              )}

              {/* Move buttons */}
              <Stack direction={"row"}>
                <IconButton
                  size={"small"}
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  sx={{ width: 22, height: 22, opacity: i === 0 ? 0.25 : 0.6 }}
                >
                  <ChevronUpIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
                <IconButton
                  size={"small"}
                  onClick={() => move(i, 1)}
                  disabled={i === local.length - 1}
                  sx={{
                    width: 22,
                    height: 22,
                    opacity: i === local.length - 1 ? 0.25 : 0.6,
                  }}
                >
                  <ChevronDownIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Ghost "Reset to default" */}
        <MuiButton
          size={"small"}
          onClick={reset}
          sx={{
            fontSize: "0.78rem",
            textTransform: "none",
            color: "text.secondary",
            p: 0,
            minWidth: 0,
            "&:hover": { bgcolor: "transparent", color: "text.primary" },
          }}
        >
          {"Reset to default"}
        </MuiButton>
        <Stack direction={"row"} spacing={1}>
          <MuiButton
            size={"small"}
            variant={"outlined"}
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "rgba(0,0,0,0.23)",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "rgba(0,0,0,0.4)",
              },
            }}
          >
            {"Cancel"}
          </MuiButton>
          <Button
            size={"small"}
            color={"secondary"}
            sx={{ textTransform: "none" }}
            onClick={apply}
          >
            {"Apply"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

// ─── Row menu ─────────────────────────────────────────────────────────────────

const RowMenu = ({
  item,
  onViewReport,
  onArchive,
  onMarkReviewed,
  onCreateSubmission,
}) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton
        size={"small"}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          color: "text.disabled",
          opacity: 0,
          ".MuiTableRow-root:hover &": { opacity: 1 },
          transition: "opacity 100ms",
        }}
      >
        <MoreVertIcon fontSize={"small"} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onViewReport(item);
          }}
        >
          {"View Report"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onMarkReviewed(item, !item.triage_reviewed);
          }}
        >
          {item.triage_reviewed ? "Mark as unreviewed" : "Mark as reviewed"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onArchive(item, !item.triage_archived);
          }}
        >
          {item.triage_archived ? "Unarchive" : "Archive"}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onCreateSubmission(item);
          }}
          sx={{ gap: 1 }}
        >
          <CreateIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
          {"Create submission"}
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── Email body dialog ────────────────────────────────────────────────────────

export const EmailBodyDialog = ({ open, item, onClose, onViewTriage }) => {
  const dispatch = useDispatch();
  const [bodyText, setBodyText] = useState(null);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);

  const fileId = item?.email_body_file_id;
  const shortId =
    (item?.folder_id || item?.file_id)?.slice(0, 8)?.toUpperCase() ?? "—";
  const title = item?.folder_name || item?.file_name || "";

  useEffect(() => {
    if (!open) {
      setAttachmentsExpanded(false);
      return;
    }
    if (!fileId) return;
    setBodyText(null);
    dispatch(
      filesApi.endpoints.fileUrl.initiate(fileId, { forceRefetch: true })
    )
      .unwrap()
      .then((res) => fetch(res.url))
      .then((r) => r.text())
      .then((text) => setBodyText(sanitizeHtml(text)))
      .catch(() => setBodyText(""));
  }, [open, fileId, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"md"}
      fullWidth
      onClick={(e) => e.stopPropagation()}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: "22px",
          py: "13px",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
          <EmailIcon sx={{ fontSize: "0.8rem", color: "text.disabled" }} />
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: "text.disabled",
            }}
          >
            {"Original Email"}
          </Typography>
          <Typography sx={{ color: "text.disabled", lineHeight: 1 }}>
            {"·"}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "monospace",
              color: "text.disabled",
            }}
          >
            {shortId}
          </Typography>
        </Stack>
        <Stack direction={"row"} spacing={0.25} alignItems={"center"}>
          <IconButton
            size={"small"}
            onClick={onClose}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Title bar ──────────────────────────────────────────────────── */}
      {title && (
        <Box
          sx={{
            px: "22px",
            py: "10px",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              lineHeight: 1.3,
            }}
            noWrap
          >
            {title}
          </Typography>
          {item?.ingestion_fields?.insured?.value && (
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
            >
              {[item.ingestion_fields.insured.value, item.triage_ruleset_name]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Attachments bar ────────────────────────────────────────────── */}
      {(item?.current_files?.length > 0 ||
        item?.attachment_files?.length > 0) &&
        (() => {
          // current_files is the item's documents NOW; attachment_files is what the last
          // RUN read, and attaching does not touch it — so the count used to stay wrong
          // until a re-run and the amber pill was the only confirmation an attach landed.
          // Merged rather than swapped because only the run's record carries a file_url,
          // which is what makes a chip downloadable.
          const runFiles = item.attachment_files || [];
          const urlByName = new Map(
            runFiles.map((f) => [f.file_name, f.file_url])
          );
          const readIds = new Set(item.triage_read_file_ids || []);
          const files = item.current_files?.length
            ? item.current_files.map((f) => ({
                ...f,
                file_url: urlByName.get(f.file_name),
                read: readIds.size === 0 || readIds.has(f.file_id),
              }))
            : runFiles.map((f) => ({ ...f, read: true }));
          const readCount = files.filter((f) => f.read).length;
          // Measure how many chips fit in one row: show first row, collapse rest
          const FIRST_ROW_MAX = 4;
          const overflowCount = files.length - FIRST_ROW_MAX;
          const visibleFiles = attachmentsExpanded
            ? files
            : files.slice(0, FIRST_ROW_MAX);

          return (
            <Box
              sx={{
                px: "22px",
                py: "8px",
                borderBottom: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
              }}
            >
              {/* Header row: icon + count + expand toggle */}
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mb={visibleFiles.length > 0 ? 0.75 : 0}
              >
                <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                  <AttachIcon
                    sx={{ fontSize: "0.75rem", color: "text.disabled" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "text.disabled",
                    }}
                  >
                    {readCount === files.length
                      ? `${files.length} document${
                          files.length !== 1 ? "s" : ""
                        }`
                      : `${readCount} of ${files.length} read by this report`}
                  </Typography>
                </Stack>
                {overflowCount > 0 && (
                  <Box
                    component={"button"}
                    onClick={() => setAttachmentsExpanded((v) => !v)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      color: "text.secondary",
                      fontSize: 11,
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    {attachmentsExpanded ? (
                      <>
                        <ChevronUpIcon sx={{ fontSize: "0.85rem" }} />
                        {"Show less"}
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon sx={{ fontSize: "0.85rem" }} />
                        {`${overflowCount} more`}
                      </>
                    )}
                  </Box>
                )}
              </Stack>

              {/* Chip row */}
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={0.75}
                flexWrap={"wrap"}
                useFlexGap
              >
                {visibleFiles.map((f) => (
                  <Box
                    key={f.file_name}
                    component={f.file_url ? "button" : "div"}
                    onClick={
                      f.file_url
                        ? () => {
                            const response = fetch(f.file_url);
                            response
                              .then((r) => r.blob())
                              .then((blob) => {
                                const blobUrl = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = blobUrl;
                                a.download = f.file_name;
                                a.click();
                                URL.revokeObjectURL(blobUrl);
                              });
                          }
                        : undefined
                    }
                    sx={(theme) => ({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      px: "8px",
                      py: "3px",
                      borderRadius: "4px",
                      fontSize: 11.5,
                      color: "text.secondary",
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${theme.palette.divider}`,
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      background: "none",
                      fontFamily: "inherit",
                      ...(f.file_url && {
                        cursor: "pointer",
                        "&:hover": {
                          color: "primary.main",
                          borderColor: "primary.light",
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                      }),
                    })}
                  >
                    <AttachIcon
                      sx={{
                        fontSize: "0.7rem",
                        color: f.read ? "text.disabled" : "warning.main",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      component={"span"}
                      sx={{
                        fontSize: 11.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.file_name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })()}

      {/* ── Email body ─────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: "hidden", minHeight: 360 }}>
        {bodyText === null ? (
          <Box sx={{ p: 3 }}>
            <Typography variant={"body2"} color={"text.secondary"}>
              {"Loading…"}
            </Typography>
          </Box>
        ) : (
          <Box
            component={"iframe"}
            srcDoc={bodyText}
            sandbox={""}
            title={"Email body"}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 400,
              border: "none",
              display: "block",
            }}
          />
        )}
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: "22px",
          py: "12px",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Typography
          variant={"caption"}
          color={"text.secondary"}
          sx={{ fontSize: 11.5 }}
        >
          <Box
            component={"span"}
            sx={{
              color: "text.primary",
              mr: 0.5,
              fontFamily: "monospace",
              fontSize: "0.75rem",
            }}
          >
            {"⊙"}
          </Box>
          {
            "Source for extracted fields. Click a rule's citation to jump to the exact line."
          }
        </Typography>
        <Stack direction={"row"} spacing={1}>
          <MuiButton
            size={"small"}
            variant={"outlined"}
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "rgba(0,0,0,0.23)",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "rgba(0,0,0,0.4)",
              },
            }}
          >
            {"Close"}
          </MuiButton>
          {onViewTriage && (
            <Button
              size={"small"}
              color={"secondary"}
              sx={{ textTransform: "none" }}
              onClick={onViewTriage}
            >
              {"View triage report"}
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

// ─── Cell renderer ────────────────────────────────────────────────────────────

const renderCell = (item, colKey, submissionNameById) => {
  switch (colKey) {
    case "ruleset":
      return item.triage_ruleset_name ? (
        <TagChip>{item.triage_ruleset_name}</TagChip>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );

    case "received":
      return item.triage_completed_at ? (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: "text.secondary",
          }}
          noWrap
        >
          {formatDate(item.triage_completed_at)}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );

    case "score":
      return (
        <ScoreBar
          passed={item._effective.passed_count}
          total={item._effective.total_count}
        />
      );

    case "weighted":
      // Withheld when every rule weighs the same — the figure would only restate the
      // fraction in the Score column beside it.
      return (
        <WeightedBar
          score={
            item.weights_informative && typeof item.weighted_score === "number"
              ? item.weighted_score
              : null
          }
          threshold={item._threshold}
        />
      );

    case "status":
      return (
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          <StatusPill
            outcome={item._effective.overall_outcome}
            overridden={!!item._effective.verdict_overridden}
          />
          {item.triage_stale && (
            <Tooltip title={STALE_REPORT_HINT} arrow>
              <Box
                sx={(theme) => ({
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: theme.palette.warning.main,
                  flexShrink: 0,
                })}
              />
            </Tooltip>
          )}
        </Stack>
      );

    case "insured": {
      const v = item.ingestion_fields?.insured?.value;
      return v ? (
        <Typography variant={"body2"} noWrap sx={{ maxWidth: 200 }}>
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "industry_class": {
      const v = item.ingestion_fields?.industryClass?.value;
      return v ? (
        <Typography
          variant={"body2"}
          noWrap
          sx={{ maxWidth: 160, color: "text.secondary" }}
        >
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "inception":
    case "expiration": {
      const v = item.ingestion_fields?.[colKey]?.value;
      return v ? (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: "text.secondary",
          }}
        >
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "folder":
      return item.folder_name ? (
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          <FolderOpenIcon sx={{ fontSize: 13, color: "primary.main" }} />
          <Typography variant={"body2"} noWrap sx={{ maxWidth: 140 }}>
            {item.folder_name}
          </Typography>
        </Stack>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );

    case "brokerage": {
      const b = item.ingestion_fields?.brokerage?.value;
      return b != null ? (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: "text.secondary",
          }}
        >
          {`${b}%`}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "maximum_possible_loss": {
      const v = getIngestionValue(item.ingestion_fields?.maximumPossibleLoss);
      return v ? (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: "text.secondary",
          }}
          noWrap
        >
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "naics": {
      const v = getIngestionValue(item.ingestion_fields?.naics);
      return v ? (
        <Typography
          variant={"body2"}
          noWrap
          sx={{ maxWidth: 160, color: "text.secondary" }}
        >
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "transaction": {
      const v = item.ingestion_fields?.transaction?.value;
      return v ? (
        <Typography variant={"body2"} color={"text.secondary"}>
          {v}
        </Typography>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
    }

    case "submission":
      return item.submission_ids?.length > 0 ? (
        <Stack spacing={0.5} alignItems={"flex-start"}>
          {item.submission_ids.map((sid) => (
            <Tooltip key={sid} title={sid} arrow>
              <Link
                to={`/submissions/${sid}/inputs/program`}
                style={{ textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}
              >
                <Chip
                  label={submissionNameById[sid] ?? sid.slice(0, 8)}
                  size={"small"}
                  color={"primary"}
                  variant={"outlined"}
                  sx={{ fontSize: "0.68rem", cursor: "pointer", maxWidth: 160 }}
                />
              </Link>
            </Tooltip>
          ))}
        </Stack>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );

    case "reviewed":
      return item.triage_reviewed ? (
        <Tooltip
          title={
            item.triage_reviewed_at
              ? `Reviewed ${formatDate(item.triage_reviewed_at)}`
              : "Reviewed"
          }
          arrow
        >
          <ReviewedIcon sx={{ fontSize: "0.95rem", color: "success.main" }} />
        </Tooltip>
      ) : (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );

    default:
      return (
        <Typography variant={"caption"} color={"text.disabled"}>
          {"—"}
        </Typography>
      );
  }
};

// ─── Row ordering ──────────────────────────────────────────────────────────────

// The value a column sorts on. Exported with sortRows so the ordering can be tested
// without mounting the table: an underwriter sorts by weighted score to find the worst
// submissions first, and the null handling is the part that regresses silently — a
// missing figure read as 0 would bury the unscored items among genuinely bad ones.
export const sortValueFor = (item, key) => {
  switch (key) {
    case "received":
      return item.triage_completed_at;
    case "score":
      return item._effective.total_count > 0
        ? item._effective.passed_count / item._effective.total_count
        : 0;
    case "weighted":
      // Items with no informative figure sort last in both directions rather than
      // reading as zero.
      return item.weights_informative ? item.weighted_score : null;
    case "status":
      return item._effective.overall_outcome;
    case "insured":
      return item.ingestion_fields?.insured?.value;
    default:
      return item[key];
  }
};

export const sortRows = (items, { key, dir }) => {
  const m = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const av = sortValueFor(a, key);
    const bv = sortValueFor(b, key);
    if (av == null) return 1;
    if (bv == null) return -1;
    return av > bv ? m : av < bv ? -m : 0;
  });
};

// ─── Carry-forward documents ───────────────────────────────────────────────────

// The documents a submission created from this item attaches up front. With the
// "carry forward all documents" tag off this is nothing: saving attaches only the file
// the carried-over fields were recorded against. attachable_file_ids is the server's list
// of the item's documents the files service can serve, so ZIP members (ids only
// ai_agents knows) never reach the submission, and a server that predates the list
// attaches nothing extra rather than guessing.
export const documentsToAttach = (item, attachAll) =>
  attachAll
    ? [...new Set((item?.attachable_file_ids || []).filter(Boolean))]
    : [];

// ─── Main component ────────────────────────────────────────────────────────────

const TriagePipeline = () => {
  // ── Data fetching ──────────────────────────────────────────────────────────
  const userConfig = useSelector(userSelectors.selectUserConfig);
  const triageEnabled =
    userConfig.componentVisibility?.[SUBMISSION_TRIAGE] ?? false;
  const carryForwardEnabled =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_PAGE] ?? false;
  const showConfidence =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_CONFIDENCE] ?? false;
  const carryForwardEditEnabled =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_EDIT] ?? false;
  const agentSearchFlagEnabled =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_AGENT_SEARCH] ?? false;
  const carryForwardAllDocuments =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_ALL_DOCUMENTS] ?? false;

  const { data, isLoading } = useGetTriagePipelineQuery(undefined, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 10000,
    skip: !triageEnabled,
  });
  const [archiveTriageResult] = useArchiveTriageResultMutation();
  const [markTriageReviewed] = useMarkTriageReviewedMutation();
  const [markFileAsUsed] = useMarkFileAsUsedMutation();
  const [agentSearchIngestionFields] = useAgentSearchIngestionFieldsMutation();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const store = useStore();

  const { data: preferences } = useRetrievePreferencesQuery();

  // Master switch from the rulesets config panel; the reviewer picks the fields to verify.
  const agentSearchConfig = agentSearchFlagEnabled
    ? preferences?.agentSearchIngestionFields ?? null
    : null;
  const [
    updatePreferences,
    { isLoading: isSavingLayout },
  ] = useUpdatePreferencesMutation();

  // ── Table density ──────────────────────────────────────────────────────────
  const [compact, setCompact] = useState(false);
  const density = compact ? DENSITY.compact : DENSITY.comfortable;

  // ── Stats row ──────────────────────────────────────────────────────────────
  const [statsCollapsed, setStatsCollapsed] = useState(false);

  // ── Column state ───────────────────────────────────────────────────────────
  const savedColumns = useMemo(
    () => mergeColumns(preferences?.[PREFS_KEY_COLUMNS]),
    [preferences]
  );
  const [columns, setColumns] = useState(null);
  const prefsLoaded = useRef(false);
  useEffect(() => {
    if (!prefsLoaded.current && preferences !== undefined) {
      setColumns(mergeColumns(preferences?.[PREFS_KEY_COLUMNS]));
      setCompact(Boolean(preferences?.[PREFS_KEY_COMPACT]));
      setStatsCollapsed(Boolean(preferences?.[PREFS_KEY_STATS_COLLAPSED]));
      prefsLoaded.current = true;
    }
  }, [preferences]);
  const displayColumns = columns ?? savedColumns;
  const visibleColumns = useMemo(
    () => displayColumns.filter((c) => c.visible),
    [displayColumns]
  );

  const layoutDirty =
    columns !== null &&
    JSON.stringify(columns.map((c) => ({ key: c.key, visible: c.visible }))) !==
      JSON.stringify(
        savedColumns.map((c) => ({ key: c.key, visible: c.visible }))
      );

  const handleSaveLayout = async () => {
    try {
      await updatePreferences({
        ...preferences,
        [PREFS_KEY_COLUMNS]: columns.map(({ key, visible }) => ({
          key,
          visible,
        })),
      }).unwrap();
      setColumns(null); // clears dirty state immediately; displayColumns falls back to savedColumns
      enqueueSnackbar("Column layout saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(`Failed to save layout: ${e.message}`, {
        variant: "error",
      });
    }
  };

  const handleDiscardLayout = () => setColumns(null); // resets to savedColumns immediately

  // View preferences persist on toggle rather than behind Save — they are not layout edits.
  const toggleViewPref = async (key, value, setter, label) => {
    // The POST replaces the whole preference object, so sending it before the GET has
    // resolved would spread `undefined` and wipe every other preference the user has.
    if (!preferences) return;
    setter(value);
    try {
      await updatePreferences({ ...preferences, [key]: value }).unwrap();
    } catch (e) {
      setter(!value);
      enqueueSnackbar(`Could not save ${label}: ${e.message}`, {
        variant: "error",
      });
    }
  };

  const handleToggleCompact = () =>
    toggleViewPref(PREFS_KEY_COMPACT, !compact, setCompact, "density");

  const handleToggleStats = () =>
    toggleViewPref(
      PREFS_KEY_STATS_COLLAPSED,
      !statsCollapsed,
      setStatsCollapsed,
      "stats visibility"
    );

  // ── Ruleset editor ref (exposes save() to dialog footer) ───────────────────
  const rulesetsRef = useRef(null);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [hideArchived, setHideArchived] = useState(true);
  const [hideUsed, setHideUsed] = useState(false);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [editRulesetsOpen, setEditRulesetsOpen] = useState(false);
  const [rulesetsCount, setRulesetsCount] = useState(0);
  const [emailDialogItem, setEmailDialogItem] = useState(null);
  const [carryForwardItem, setCarryForwardItem] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    status: [],
    ruleset: [],
    broker: [],
    reviewed: null, // null = all, true = reviewed only, false = unreviewed only
    dateFrom: "",
    dateTo: "",
  });
  const [sort, setSort] = useState({ key: "received", dir: "desc" });

  // Side panel state
  const [viewReportItem, setViewReportItem] = useState(null);
  const {
    data: reportData,
    isFetching: reportLoading,
    refetch: refetchTriageResult,
  } = useGetTriageResultQuery(viewReportItem?.fileId, {
    skip: !viewReportItem?.fileId,
  });
  const [rerunSubmissionTriage] = useRerunSubmissionTriageMutation();
  const [detachIngestionFiles] = useDetachIngestionFilesMutation();
  const [ingestionFileUrl] = useIngestionFileUrlMutation();
  const [attachFilesItem, setAttachFilesItem] = useState(null);
  // Files dropped straight onto the drawer's Documents block, handed to the attach dialog
  // so a drop and a click share its validation and confirmation.
  const [attachFilesDropped, setAttachFilesDropped] = useState(null);

  // ── Derived data ───────────────────────────────────────────────────────────
  const items = useMemo(() => data?.items ?? [], [data]);

  // The in-scope threshold as the ruleset reads NOW, not as the run recorded it: editing
  // it reclassifies every submission on the next render, with no re-run.
  const thresholdByRulesetId = useMemo(() => {
    const rulesets =
      preferences?.triageRulesets ?? preferences?.triage_rulesets ?? [];
    return Object.fromEntries(
      rulesets.map((r) => [r.id, r.in_scope_threshold ?? null])
    );
  }, [preferences]);

  const effectiveItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        _effective: applyOverrides(
          {
            overall_outcome: item.overall_outcome,
            passed_count: item.passed_count,
            total_count: item.total_count,
            weighted_score: item.weighted_score,
          },
          item.triage_overrides,
          thresholdByRulesetId[item.triage_ruleset_id] ?? null,
          item.triage_verdict_override ?? null
        ),
        _threshold: thresholdByRulesetId[item.triage_ruleset_id] ?? null,
      })),
    [items, thresholdByRulesetId]
  );

  // Always reflect the latest pipeline data for the open drawer item
  const viewReportLiveItem = useMemo(() => {
    if (!viewReportItem) return null;
    return (
      effectiveItems.find((i) =>
        viewReportItem.folderId
          ? i.folder_id === viewReportItem.folderId
          : i.file_id === viewReportItem.fileId
      ) ?? viewReportItem.item
    );
  }, [viewReportItem, effectiveItems]);

  // The ruleset this report was scored against, resolved from preferences the same way the
  // re-run resolves it. Passed to the drawer so a scored rule can show what it requires;
  // the report itself records outcomes, never the rule text.
  const viewReportRuleset = useMemo(() => {
    const rulesets =
      preferences?.triageRulesets ?? preferences?.triage_rulesets ?? [];
    if (rulesets.length === 0) return null;
    return (
      rulesets.find((r) => r.id === viewReportLiveItem?.triage_ruleset_id) ??
      rulesets.find(
        (r) => r.name === viewReportLiveItem?.triage_ruleset_name
      ) ??
      null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preferences,
    viewReportLiveItem?.triage_ruleset_id,
    viewReportLiveItem?.triage_ruleset_name,
  ]);

  // The list polls every 10s, so a background re-triage lands here first. Without this the
  // row's amber dot and the drawer's stale pill both clear from polled data while the
  // drawer's report body keeps rendering the previous run — an outdated report presented as
  // current, which is the one state the stale flag exists to prevent.
  useEffect(() => {
    const liveRunId = viewReportLiveItem?.triage_run_id;
    const loadedRunId = reportData?.triage_run_id;
    if (liveRunId && loadedRunId && liveRunId !== loadedRunId) {
      refetchTriageResult();
    }
  }, [
    viewReportLiveItem?.triage_run_id,
    reportData?.triage_run_id,
    refetchTriageResult,
  ]);

  const stats = useMemo(
    () => (effectiveItems.length > 0 ? computeStats(effectiveItems) : null),
    [effectiveItems]
  );

  const availableRulesets = useMemo(
    () => [
      ...new Set(
        effectiveItems.map((i) => i.triage_ruleset_name).filter(Boolean)
      ),
    ],
    [effectiveItems]
  );

  const availableBrokers = useMemo(
    () => [
      ...new Set(
        effectiveItems
          .map((i) => i.ingestion_fields?.broker?.value)
          .filter(Boolean)
      ),
    ],
    [effectiveItems]
  );

  const activeFilterCount =
    (filters.status?.length || 0) +
    (filters.ruleset?.length || 0) +
    (filters.broker?.length || 0) +
    (filters.reviewed !== null ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0);

  const filtered = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    return effectiveItems.filter((item) => {
      if (hideArchived && item.triage_archived) return false;
      if (hideUsed && item.used_in_submission) return false;
      if (hideReviewed && item.triage_reviewed) return false;
      if (
        filters.status.length &&
        !filters.status.includes(item._effective.overall_outcome)
      )
        return false;
      if (
        filters.ruleset.length &&
        !filters.ruleset.includes(item.triage_ruleset_name)
      )
        return false;
      if (filters.broker?.length) {
        const broker = item.ingestion_fields?.broker?.value;
        if (!broker || !filters.broker.includes(broker)) return false;
      }
      if (filters.reviewed !== null && filters.reviewed !== undefined) {
        if (Boolean(item.triage_reviewed) !== filters.reviewed) return false;
      }
      if (filters.dateFrom) {
        const d = item.triage_completed_at?.slice(0, 10);
        if (!d || d < filters.dateFrom) return false;
      }
      if (filters.dateTo) {
        const d = item.triage_completed_at?.slice(0, 10);
        if (!d || d > filters.dateTo) return false;
      }
      if (q) {
        const name = (item.file_name ?? "").toLowerCase();
        const insured = (
          item.ingestion_fields?.insured?.value ?? ""
        ).toLowerCase();
        const ruleset = (item.triage_ruleset_name ?? "").toLowerCase();
        if (!name.includes(q) && !insured.includes(q) && !ruleset.includes(q))
          return false;
      }
      return true;
    });
  }, [
    effectiveItems,
    searchText,
    filters,
    hideArchived,
    hideUsed,
    hideReviewed,
  ]);

  const sortedRows = useMemo(() => {
    return sortRows(filtered, sort);
  }, [filtered, sort]);

  const handleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" }
    );
  };

  // ── Pagination ─────────────────────────────────────────────────────────────
  const ROWS_PER_PAGE = 25;
  useEffect(() => {
    setPage(0);
  }, [filtered]);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
  const paginatedRows = sortedRows.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE
  );

  // ── Handlers ───────────────────────────────────────────────────────────────

  // Per-rule sourcing from the ruleset that triaged this item — it sets a field's mode
  // and preference. Which fields get verified is the reviewer's pick in the dialog.
  const carryForwardSourcing = useMemo(() => {
    if (!agentSearchConfig?.enabled || !carryForwardItem) {
      return { modes: {}, sourcing: {}, descriptions: {} };
    }
    const rulesets =
      preferences?.triageRulesets ?? preferences?.triage_rulesets ?? [];
    // By id first — renaming a ruleset must not silently disable its web search. The
    // name fallback covers items triaged before ids were stored on pipeline rows.
    const used =
      rulesets.find((r) => r.id === carryForwardItem.triage_ruleset_id) ??
      rulesets.find((r) => r.name === carryForwardItem.triage_ruleset_name);
    return {
      modes: agentSearchModesForRules(used?.rules),
      sourcing: sourcingByIngestionKey(used?.rules),
      descriptions: ruleDescriptionsByIngestionKey(used?.rules),
    };
  }, [agentSearchConfig, carryForwardItem, preferences]);

  // Memoised for its IDENTITY, not its cost. The review dialog rebuilds its field list
  // when this prop changes reference and resets every draft, approval and web check with
  // it — so an inline literal here wiped the reviewer's work on each 10s pipeline poll.
  const reviewAgentSearchConfig = useMemo(
    () =>
      agentSearchConfig
        ? { ...agentSearchConfig, ...carryForwardSourcing }
        : null,
    [agentSearchConfig, carryForwardSourcing]
  );

  // Lets the review page check a proposed enum value against the same option list
  // the submission form's selector uses, before the reviewer applies it.
  const resolveEnumForReview = useCallback(
    async (fieldKey, value) => {
      if (!isEnumBackedField(fieldKey)) return null;
      const state = store.getState();
      return resolveEnumField(fieldKey, value, {
        dispatch,
        claimsApi,
        industryClassChoices:
          state.temp?.exposureMetrics?.industryClassChoices ?? [],
        currencyCodesList: selectCurrencyCodes(state) ?? [],
      });
    },
    [store, dispatch]
  );

  // Creates the submission, carrying over only the chosen ingestion fields.
  // `fieldKeys` is a list of backend ingestion_fields keys; omit to carry all.
  const commitCreateSubmission = useCallback(
    async (
      item,
      fieldKeys,
      decisions,
      fieldOverrides,
      fieldSources,
      research
    ) => {
      try {
        const fields = item.ingestion_fields || {};
        const keys = fieldKeys ?? CARRY_FORWARD_FIELDS;
        // Representative file for folder/email items; attachment_files carry only
        // {file_name, file_url}, so there is no usable fallback.
        const fileId = item.file_id || null;
        const runId = item.ingestion_run_id || null;
        // Collect the chosen values, then cross-check the enum-backed ones before
        // anything reaches program state. Web values and manual edits arrive as
        // display strings ("482111 – Line-Haul Railroads", "Rail Transportation"),
        // and the selectors can only render a canonical option.
        const chosen = {};
        keys.forEach((backendKey) => {
          if (!CARRY_FORWARD_FIELDS.includes(backendKey)) return;
          const overridden =
            fieldOverrides != null &&
            Object.prototype.hasOwnProperty.call(fieldOverrides, backendKey);
          // Pass the identical reference when unedited — the tracker diffs with
          // strict ===, so a clone of an object value would look changed.
          const v = overridden
            ? fieldOverrides[backendKey]
            : fields[backendKey]?.value;
          if (v != null) chosen[backendKey] = v;
        });

        const state = store.getState();
        const { values: checked, issues } = await resolveEnumFields(chosen, {
          dispatch,
          claimsApi,
          industryClassChoices:
            state.temp?.exposureMetrics?.industryClassChoices ?? [],
          currencyCodesList: selectCurrencyCodes(state) ?? [],
        });
        if (issues.length > 0) {
          enqueueSnackbar(describeEnumIssues(issues, FIELD_LABELS), {
            variant: "warning",
          });
        }

        // The tracker is module-global and only cleared on save, so without this a
        // submission created straight after another carries the earlier one's fields.
        clearFieldUsage();

        const programFields = {};
        keys.forEach((key) => {
          if (!CARRY_FORWARD_FIELDS.includes(key)) return;
          const aiValue = fields[key]?.value;
          const v = checked[key];
          if (v != null) programFields[key] = v;
          // Same two calls the upload drawer makes; one vocabulary means the records
          // join up with later edits on the submission form without translation.
          if (fileId && aiValue != null) {
            recordSuggestionSelection(key, aiValue, fileId, runId);
            recordFieldValue(key, v ?? "");
          }
        });

        // Both versions of every field an agent search covered — recorded outside the
        // carry-forward loop on purpose: a field the reviewer rejected, or one only the
        // web answered, still has provenance worth keeping.
        Object.entries(fieldSources || {}).forEach(([key, record]) => {
          if (CARRY_FORWARD_FIELDS.includes(key))
            recordFieldSources(key, record);
        });
        recordWebResearch(research);

        const prevId = persistenceSelectors.selectSubmissionId(
          store.getState()
        );
        await dispatch(persistenceActions.newSubmission({ navigate }));
        const newId = persistenceSelectors.selectSubmissionId(store.getState());

        if (
          newId &&
          newId !== prevId &&
          Object.keys(programFields).length > 0
        ) {
          dispatch(
            programActions.updateProgram({
              ...programFields,
              createdFromAI: true,
              aiUsedFields: Object.keys(programFields),
            })
          );
        }

        // Into the submission's state like the fields above, so the first save
        // persists them. That save skips files the attachments already account for,
        // so one the user deletes or moves before saving stays deleted or moved.
        const attachIds =
          newId && newId !== prevId
            ? documentsToAttach(item, carryForwardAllDocuments)
            : [];
        attachIds.forEach((id) =>
          dispatch(attachmentsActions.fileUploaded(id))
        );

        // One write carrying field usage AND the approve/reject decisions, so
        // neither this write nor the save-time one can be a strict subset of the
        // other — the backend does a full replace, rescuing reviewed_fields whole
        // and merging field_sources per field.
        let representativeMarked = false;
        if (newId && newId !== prevId && fileId) {
          const usage = buildFieldUsageByFile(fileId)[fileId] || null;
          const fieldUsage = decisions
            ? { ...(usage || {}), reviewed_fields: decisions }
            : usage;
          if (fieldUsage) {
            await markFileAsUsed({ fileId, submissionId: newId, fieldUsage })
              .unwrap()
              .catch(() => {});
            representativeMarked = true;
          }
        }
        // Every attached document is used in the submission too. One at a time:
        // each mark rewrites the whole registry, so concurrent ones can lose writes.
        for (const id of attachIds) {
          if (id === fileId && representativeMarked) continue;
          await markFileAsUsed({
            fileId: id,
            submissionId: newId,
            fieldUsage: null,
          })
            .unwrap()
            .catch(() => {});
        }
      } catch (e) {
        enqueueSnackbar(`Failed to create submission: ${e.message}`, {
          variant: "error",
        });
      }
    },
    [navigate, store, enqueueSnackbar, markFileAsUsed, carryForwardAllDocuments] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // With the flag on, show the carry-forward picker first; otherwise carry all.
  const handleCreateSubmission = useCallback(
    (item) => {
      if (carryForwardEnabled) {
        setCarryForwardItem(item);
      } else {
        commitCreateSubmission(item);
      }
    },
    [carryForwardEnabled, commitCreateSubmission]
  );

  // Download a field's source document from the review page. Source citations
  // only carry a filename, so resolve it to a file_id from the item's files.
  const downloadSource = useCallback(
    async (loc) => {
      const fileId =
        loc?.file_id ||
        matchFileByName(carryForwardItem?.attachment_files, loc?.filename)
          ?.file_id;
      if (!fileId) {
        enqueueSnackbar(
          `Source file "${loc?.filename}" isn't available to download`,
          { variant: "warning" }
        );
        return;
      }
      try {
        await downloadIngestionSource(fileId, loc.filename, ingestionFileUrl);
      } catch (e) {
        enqueueSnackbar(`Couldn't download ${loc.filename}`, {
          variant: "error",
        });
      }
    },
    [carryForwardItem, enqueueSnackbar, ingestionFileUrl]
  );

  // Removing a document a user attached by mistake needs confirming: it changes what every
  // future re-run reads, and the stored report stops describing the item. The dialog lives
  // in the app rather than window.confirm, which renders a browser chrome alert.
  const [detachTarget, setDetachTarget] = useState(null);
  const [isDetaching, setIsDetaching] = useState(false);

  const handleDetachFile = useCallback((item, file) => {
    if (!item?.folder_id || !file?.file_id) return;
    setDetachTarget({ item, file });
  }, []);

  const confirmDetach = useCallback(async () => {
    if (!detachTarget) return;
    const { item, file } = detachTarget;
    setIsDetaching(true);
    try {
      await detachIngestionFiles({
        folderId: item.folder_id,
        fileIds: [file.file_id],
      }).unwrap();
      enqueueSnackbar(
        `Removed ${file.file_name} — re-run triage to update the report`,
        { variant: "success" }
      );
      setDetachTarget(null);
      await refetchTriageResult();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setIsDetaching(false);
    }
  }, [
    detachTarget,
    detachIngestionFiles,
    enqueueSnackbar,
    refetchTriageResult,
  ]);

  const handleArchive = (item, archived) => {
    archiveTriageResult({
      folderId: item.folder_id || null,
      fileId: item.folder_id ? null : item.file_id,
      archived,
    });
  };

  const handleMarkReviewed = (item, reviewed) => {
    markTriageReviewed({
      folderId: item.folder_id || null,
      fileId: item.folder_id ? null : item.file_id,
      reviewed,
    });
  };

  // Download URLs for documents the triage service could not read. It cannot sign for the
  // prefix app-uploaded documents live under, and deliberately has no access to the files
  // service — this page does, so it mints them here and hands them over.
  const freshUrlsFor = useCallback(
    async (fileIds) => {
      const results = await Promise.all(
        fileIds.map((fileId) =>
          dispatch(
            filesApi.endpoints.fileUrl.initiate(fileId, { forceRefetch: true })
          )
            .unwrap()
            .then((res) => [fileId, res?.url])
            // One document we cannot refresh must not lose the others: the retry runs
            // with whatever we did get.
            .catch(() => [fileId, null])
        )
      );
      return Object.fromEntries(results.filter(([, url]) => url));
    },
    [dispatch]
  );

  const handleRerunTriage = useCallback(async () => {
    if (!viewReportItem) return;
    // The server derives the file set from the registry, so the re-run covers
    // every file currently attached to the item — including ones added after
    // the original run. Resolve the CURRENT ruleset from preferences (id first,
    // name for items triaged before ids were stored) so rule edits take effect;
    // when none matches, the backend falls back to the item's stored snapshot.
    const rulesets =
      preferences?.triageRulesets ?? preferences?.triage_rulesets ?? [];
    const ruleset =
      rulesets.find((r) => r.id === viewReportLiveItem?.triage_ruleset_id) ??
      rulesets.find(
        (r) => r.name === viewReportLiveItem?.triage_ruleset_name
      ) ??
      rulesets.find((r) => r.isDefault) ??
      rulesets[0];
    const runTriage = (fileUrls) =>
      rerunSubmissionTriage({
        fileId: viewReportItem.folderId ? null : viewReportItem.fileId,
        folderId: viewReportItem.folderId,
        ruleset,
        // No mode: the deployment's configured TRIAGE_RERUN_MODE governs every re-run
        // from the UI. The endpoint still accepts a per-request override for API callers.
        fileUrls,
      }).unwrap();

    try {
      let response = await runTriage();
      // A stored download URL is signed with temporary credentials and dies well before
      // its nominal expiry. The service cannot sign for the prefix app-uploaded documents
      // live under and has no access to the files service, so it names the documents it
      // could not read and we hand it URLs we can mint.
      if (response?.unreadable_file_ids?.length > 0) {
        const fresh = await freshUrlsFor(response.unreadable_file_ids);
        if (Object.keys(fresh).length > 0) {
          response = await runTriage(fresh);
        }
      }
      await refetchTriageResult();
      if (response?.success === false) {
        enqueueSnackbar(response.error || "Triage re-run failed", {
          variant: "error",
        });
        return;
      }
      // A run can succeed with a caveat — grounded on an extraction that had not caught
      // up with the newly attached documents. Reporting success alone would hide that.
      if (response?.warning) {
        enqueueSnackbar(response.warning, { variant: "warning" });
      } else {
        enqueueSnackbar("Triage re-run complete", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  }, [
    viewReportItem,
    viewReportLiveItem,
    preferences,
    rerunSubmissionTriage,
    refetchTriageResult,
    enqueueSnackbar,
    freshUrlsFor,
  ]);

  const handleRowClick = (item) => {
    setViewReportItem({
      fileId: item.file_id,
      folderId: item.folder_id || null,
      fileName: item.file_name,
      item,
    });
  };

  // ── Keyboard shortcut: ⌘K focuses search ──────────────────────────────────
  const searchRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const SortIcon = ({ colKey }) => {
    if (sort.key !== colKey)
      return <SortNeutralIcon sx={{ fontSize: "0.8rem", opacity: 0.3 }} />;
    return sort.dir === "asc" ? (
      <SortAscIcon sx={{ fontSize: "0.8rem" }} />
    ) : (
      <SortDescIcon sx={{ fontSize: "0.8rem" }} />
    );
  };

  const SORTABLE_COLS = new Set([
    "received",
    "score",
    "weighted",
    "status",
    "insured",
    "ruleset",
  ]);

  const totalColCount = visibleColumns.length; // row actions live inside the Submission cell

  const tableScrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  // Column resizing
  const [columnWidths, setColumnWidths] = useState({});
  const resizeDragRef = useRef(null); // { key, startX, startWidth }

  const getColWidth = useCallback(
    (key) => columnWidths[key] ?? defaultColWidth(key),
    [columnWidths]
  );

  const handleResizeStart = useCallback(
    (e, colKey) => {
      e.preventDefault();
      e.stopPropagation();
      resizeDragRef.current = {
        key: colKey,
        startX: e.clientX,
        startWidth: columnWidths[colKey] ?? defaultColWidth(colKey),
      };
    },
    [columnWidths]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!resizeDragRef.current) return;
      const { key, startX, startWidth } = resizeDragRef.current;
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
    };
    const onUp = () => {
      resizeDragRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const checkScroll = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    setScrollState({
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);

  const panelOpen = Boolean(viewReportItem);
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    checkScroll();
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkScroll, visibleColumns.length, panelOpen, paginatedRows.length]);

  return (
    <Dashboard>
      <DashboardContent isFullWidth>
        {/* ── Page header + Stats row ──────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper
            variant={"outlined"}
            sx={{ borderRadius: 2, p: 2.5, mb: 0, bgcolor: "background.paper" }}
          >
            {/* Page header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: statsCollapsed ? 0 : 2.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                    mb: 0.5,
                  }}
                >
                  {"Triage Pipeline"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {
                    "Review incoming submissions, tune rulesets, and route decisions to underwriting."
                  }
                </Typography>
              </Box>
              <Stack direction={"row"} spacing={1} mt={0.5}>
                <Button
                  size={"small"}
                  color={"secondary"}
                  startIcon={
                    statsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />
                  }
                  onClick={handleToggleStats}
                  sx={{ textTransform: "none" }}
                >
                  {statsCollapsed ? "Show stats" : "Hide stats"}
                </Button>
                <Button
                  size={"small"}
                  color={"secondary"}
                  startIcon={<LayersIcon />}
                  onClick={() => setEditRulesetsOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  {"Edit rulesets"}
                </Button>
              </Stack>
            </Box>

            {/* Stats row */}
            <Collapse in={!statsCollapsed} unmountOnExit>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 1.5,
                }}
              >
                {stats
                  ? stats.map((card) => <StatCard key={card.label} {...card} />)
                  : Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        variant={"rectangular"}
                        height={96}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* ── Table card ───────────────────────────────────────────────── */}
        <Grid item xs={12}>
          {/* Layout save bar */}
          {layoutDirty && (
            <Box
              sx={(theme) => ({
                px: 2,
                py: "9px",
                mb: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderBottom: "none",
                borderRadius: "8px 8px 0 0",
              })}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant={"caption"}
                  fontWeight={500}
                  color={"text.secondary"}
                >
                  {"Column layout has unsaved changes"}
                </Typography>
              </Stack>
              <Stack direction={"row"} spacing={1}>
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={handleDiscardLayout}
                  sx={{
                    fontSize: "0.78rem",
                    textTransform: "none",
                    color: "text.primary",
                    borderColor: "rgba(0,0,0,0.23)",
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  {"Discard"}
                </MuiButton>
                <Button
                  size={"small"}
                  color={"secondary"}
                  startIcon={
                    <SaveIcon sx={{ fontSize: "0.8rem !important" }} />
                  }
                  onClick={handleSaveLayout}
                  isLoading={isSavingLayout}
                  isDisabled={isSavingLayout}
                  sx={{ textTransform: "none" }}
                >
                  {"Save layout"}
                </Button>
              </Stack>
            </Box>
          )}

          <Paper
            variant={"outlined"}
            sx={{
              borderRadius: layoutDirty ? "0 0 8px 8px" : 2,
              overflow: "hidden",
            }}
          >
            {/* ── Toolbar ───────────────────────────────────────────────── */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Search with ⌘K shortcut */}
              <OutlinedInput
                size={"small"}
                inputRef={searchRef}
                placeholder={"Search submissions, insureds"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                startAdornment={
                  <InputAdornment position={"start"}>
                    <Box
                      component={"svg"}
                      viewBox={"0 0 24 24"}
                      sx={{ width: 14, height: 14, color: "text.disabled" }}
                    >
                      <circle
                        cx={"11"}
                        cy={"11"}
                        r={"8"}
                        fill={"none"}
                        stroke={"currentColor"}
                        strokeWidth={"1.8"}
                      />
                      <line
                        x1={"21"}
                        y1={"21"}
                        x2={"16.65"}
                        y2={"16.65"}
                        stroke={"currentColor"}
                        strokeWidth={"1.8"}
                        strokeLinecap={"round"}
                      />
                    </Box>
                  </InputAdornment>
                }
                endAdornment={
                  !searchText && (
                    <InputAdornment position={"end"}>
                      <Box
                        sx={(theme) => ({
                          px: "5px",
                          py: "2px",
                          borderRadius: "4px",
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha(theme.palette.text.primary, 0.04),
                          lineHeight: 1,
                        })}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "text.disabled",
                            fontFamily: "monospace",
                            lineHeight: 1.4,
                          }}
                        >
                          {"⌘K"}
                        </Typography>
                      </Box>
                    </InputAdornment>
                  )
                }
                sx={{
                  height: 32,
                  fontSize: "0.82rem",
                  minWidth: 260,
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "6px" },
                }}
              />

              {/* Filter button */}
              <MuiButton
                size={"small"}
                variant={"outlined"}
                startIcon={
                  <FilterIcon sx={{ fontSize: "0.85rem !important" }} />
                }
                onClick={() => setFilterOpen(true)}
                sx={{
                  fontSize: "0.78rem",
                  height: 32,
                  textTransform: "none",
                  color: "text.primary",
                  borderColor: "rgba(0,0,0,0.23)",
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                    borderColor: "rgba(0,0,0,0.4)",
                  },
                }}
              >
                {"Filter"}
                {activeFilterCount > 0 && (
                  <Box
                    sx={{
                      ml: 0.75,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.5,
                    }}
                  >
                    {activeFilterCount}
                  </Box>
                )}
              </MuiButton>

              {/* Active filter chips */}
              {(filters.status || []).map((s) => (
                <Chip
                  key={s}
                  size={"small"}
                  label={
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                    >
                      <Typography
                        sx={{ fontSize: 11, color: "text.secondary" }}
                      >
                        {"Status:"}
                      </Typography>
                      <StatusPill outcome={s} />
                    </Stack>
                  }
                  onDelete={() =>
                    setFilters((f) => ({
                      ...f,
                      status: f.status.filter((x) => x !== s),
                    }))
                  }
                  sx={{ height: 26 }}
                />
              ))}
              {(filters.ruleset || []).map((r) => (
                <Chip
                  key={r}
                  size={"small"}
                  label={`Ruleset: ${r}`}
                  onDelete={() =>
                    setFilters((f) => ({
                      ...f,
                      ruleset: f.ruleset.filter((x) => x !== r),
                    }))
                  }
                  sx={{ height: 26, fontSize: "0.72rem" }}
                />
              ))}
              {(filters.broker || []).map((b) => (
                <Chip
                  key={b}
                  size={"small"}
                  label={`Broker: ${b}`}
                  onDelete={() =>
                    setFilters((f) => ({
                      ...f,
                      broker: f.broker.filter((x) => x !== b),
                    }))
                  }
                  sx={{ height: 26, fontSize: "0.72rem" }}
                />
              ))}
              {filters.reviewed !== null && filters.reviewed !== undefined && (
                <Chip
                  size={"small"}
                  label={`Reviewed: ${filters.reviewed ? "Yes" : "No"}`}
                  onDelete={() => setFilters((f) => ({ ...f, reviewed: null }))}
                  sx={{ height: 26, fontSize: "0.72rem" }}
                />
              )}
              {(filters.dateFrom || filters.dateTo) && (
                <Chip
                  size={"small"}
                  label={`Date: ${filters.dateFrom || "—"} → ${
                    filters.dateTo || "—"
                  }`}
                  onDelete={() =>
                    setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }))
                  }
                  sx={{ height: 26, fontSize: "0.72rem" }}
                />
              )}

              {/* Right-side controls */}
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                {/* Hide used */}
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={() => setHideUsed((v) => !v)}
                  startIcon={
                    <HideUsedIcon sx={{ fontSize: "0.85rem !important" }} />
                  }
                  sx={(theme) => ({
                    fontSize: "0.78rem",
                    height: 32,
                    textTransform: "none",
                    color: hideUsed ? "primary.main" : "text.primary",
                    borderColor: hideUsed ? "primary.main" : "rgba(0,0,0,0.23)",
                    bgcolor: hideUsed
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "background.paper",
                    "&:hover": {
                      bgcolor: hideUsed
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "action.hover",
                      borderColor: hideUsed
                        ? "primary.main"
                        : "rgba(0,0,0,0.4)",
                    },
                  })}
                >
                  {"Hide used"}
                </MuiButton>

                {/* Hide reviewed */}
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={() => setHideReviewed((v) => !v)}
                  startIcon={
                    <ReviewedIcon sx={{ fontSize: "0.85rem !important" }} />
                  }
                  sx={(theme) => ({
                    fontSize: "0.78rem",
                    height: 32,
                    textTransform: "none",
                    color: hideReviewed ? "primary.main" : "text.primary",
                    borderColor: hideReviewed
                      ? "primary.main"
                      : "rgba(0,0,0,0.23)",
                    bgcolor: hideReviewed
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "background.paper",
                    "&:hover": {
                      bgcolor: hideReviewed
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "action.hover",
                      borderColor: hideReviewed
                        ? "primary.main"
                        : "rgba(0,0,0,0.4)",
                    },
                  })}
                >
                  {"Hide reviewed"}
                </MuiButton>

                {/* Hide archived */}
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={() => setHideArchived((v) => !v)}
                  startIcon={
                    <HideArchivedIcon sx={{ fontSize: "0.85rem !important" }} />
                  }
                  sx={(theme) => ({
                    fontSize: "0.78rem",
                    height: 32,
                    textTransform: "none",
                    color: hideArchived ? "primary.main" : "text.primary",
                    borderColor: hideArchived
                      ? "primary.main"
                      : "rgba(0,0,0,0.23)",
                    bgcolor: hideArchived
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "background.paper",
                    "&:hover": {
                      bgcolor: hideArchived
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "action.hover",
                      borderColor: hideArchived
                        ? "primary.main"
                        : "rgba(0,0,0,0.4)",
                    },
                  })}
                >
                  {"Hide archived"}
                </MuiButton>

                {/* Compact rows */}
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={handleToggleCompact}
                  startIcon={
                    <CompactIcon sx={{ fontSize: "0.85rem !important" }} />
                  }
                  sx={(theme) => ({
                    fontSize: "0.78rem",
                    height: 32,
                    textTransform: "none",
                    color: compact ? "primary.main" : "text.primary",
                    borderColor: compact ? "primary.main" : "rgba(0,0,0,0.23)",
                    bgcolor: compact
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "background.paper",
                    "&:hover": {
                      bgcolor: compact
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "action.hover",
                      borderColor: compact ? "primary.main" : "rgba(0,0,0,0.4)",
                    },
                  })}
                >
                  {"Compact"}
                </MuiButton>

                {/* Columns */}
                <MuiButton
                  size={"small"}
                  variant={"outlined"}
                  startIcon={
                    <ColumnsIcon sx={{ fontSize: "0.85rem !important" }} />
                  }
                  onClick={() => setColPickerOpen(true)}
                  sx={{
                    fontSize: "0.78rem",
                    height: 32,
                    textTransform: "none",
                    color: "text.primary",
                    borderColor: "rgba(0,0,0,0.23)",
                    bgcolor: "background.paper",
                    gap: 0.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  {"Columns"}
                  <Box
                    sx={(theme) => ({
                      ml: 0.25,
                      minWidth: 18,
                      height: 18,
                      borderRadius: "4px",
                      bgcolor: alpha(theme.palette.text.primary, 0.07),
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: "text.secondary",
                      px: 0.5,
                    })}
                  >
                    {visibleColumns.length}
                  </Box>
                </MuiButton>
              </Box>
            </Box>

            {/* ── Table ─────────────────────────────────────────────────── */}
            <Box sx={{ position: "relative" }}>
              {/* Left edge shadow — fades in once user has scrolled right */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 28,
                  background:
                    "linear-gradient(90deg, rgba(12,10,9,0.08) 0%, rgba(12,10,9,0) 100%)",
                  pointerEvents: "none",
                  zIndex: 4,
                  opacity: scrollState.left ? 1 : 0,
                  transition: "opacity 150ms",
                }}
              />
              {/* Right edge shadow — visible when there is overflow to the right */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 28,
                  background:
                    "linear-gradient(270deg, rgba(12,10,9,0.08) 0%, rgba(12,10,9,0) 100%)",
                  pointerEvents: "none",
                  zIndex: 4,
                  opacity: scrollState.right ? 1 : 0,
                  transition: "opacity 150ms",
                }}
              />
              <TableContainer
                ref={tableScrollRef}
                onScroll={checkScroll}
                sx={{
                  "&::-webkit-scrollbar": { height: "10px" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "action.disabled",
                    borderRadius: "5px",
                    border: "2px solid",
                    borderColor: "background.paper",
                  },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                }}
              >
                <Table
                  size={"small"}
                  stickyHeader
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    tableLayout: "fixed",
                    minWidth: "100%",
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      py: density.headPadY,
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: getColWidth("file") }} />
                    {visibleColumns
                      .filter((c) => c.key !== "file")
                      .map((col) => (
                        <col
                          key={col.key}
                          style={{ width: getColWidth(col.key) }}
                        />
                      ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {/* File column always first — pinned left */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          bgcolor: "background.paper",
                          boxShadow: scrollState.left
                            ? "1px 0 0 rgba(0,0,0,0.08), 6px 0 10px -6px rgba(12,10,9,0.12)"
                            : "none",
                          fontWeight: 500,
                          fontSize: "11.5px",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                          color: "text.secondary",
                          cursor: SORTABLE_COLS.has("file")
                            ? "pointer"
                            : "default",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        {"Submission"}
                        <Box
                          onMouseDown={(e) => handleResizeStart(e, "file")}
                          sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            cursor: "col-resize",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "stretch",
                            justifyContent: "center",
                            "&:hover > span": {
                              bgcolor: "primary.main",
                              opacity: 0.7,
                            },
                          }}
                        >
                          <Box
                            component={"span"}
                            sx={{ width: "1px", bgcolor: "divider" }}
                          />
                        </Box>
                      </TableCell>

                      {visibleColumns
                        .filter((c) => c.key !== "file")
                        .map((col) => (
                          <TableCell
                            key={col.key}
                            sx={{
                              position: "relative",
                              fontWeight: 500,
                              fontSize: "11.5px",
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              cursor: SORTABLE_COLS.has(col.key)
                                ? "pointer"
                                : "default",
                              userSelect: "none",
                              "&:hover": SORTABLE_COLS.has(col.key)
                                ? { color: "text.primary" }
                                : {},
                            }}
                            onClick={() =>
                              SORTABLE_COLS.has(col.key) && handleSort(col.key)
                            }
                          >
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              spacing={0.5}
                            >
                              <span>{col.label}</span>
                              {SORTABLE_COLS.has(col.key) && (
                                <SortIcon colKey={col.key} />
                              )}
                            </Stack>
                            <Box
                              onMouseDown={(e) => handleResizeStart(e, col.key)}
                              sx={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: 6,
                                cursor: "col-resize",
                                zIndex: 1,
                                display: "flex",
                                alignItems: "stretch",
                                justifyContent: "center",
                                "&:hover > span": {
                                  bgcolor: "primary.main",
                                  opacity: 0.7,
                                },
                              }}
                            >
                              <Box
                                component={"span"}
                                sx={{ width: "1px", bgcolor: "divider" }}
                              />
                            </Box>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading &&
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: totalColCount }).map(
                            (__, j) => (
                              <TableCell key={j}>
                                <Skeleton variant={"text"} width={"80%"} />
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      ))}

                    {!isLoading && sortedRows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={totalColCount}
                          align={"center"}
                          sx={{ py: 6 }}
                        >
                          <Typography
                            variant={"body2"}
                            color={"text.secondary"}
                          >
                            {"No triage results match these filters."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {paginatedRows.map((item) => {
                      const isSelected =
                        viewReportItem?.fileId === item.file_id ||
                        viewReportItem?.folderId === item.folder_id;
                      return (
                        <TableRow
                          key={item.folder_id || item.file_id}
                          hover
                          onClick={() => handleRowClick(item)}
                          sx={{
                            cursor: "pointer",
                            opacity: item.triage_archived ? 0.5 : 1,
                            bgcolor: isSelected
                              ? (theme) =>
                                  alpha(theme.palette.primary.main, 0.04)
                              : "inherit",
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.text.primary, 0.03),
                            },
                            "&:hover .row-actions": { opacity: 1 },
                            "& .MuiTableCell-root": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              py: density.cellPadY,
                            },
                          }}
                        >
                          {/* File cell — always first, pinned left */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 1,
                              bgcolor: isSelected
                                ? (theme) =>
                                    alpha(theme.palette.primary.main, 0.04)
                                : "background.paper",
                              boxShadow: scrollState.left
                                ? "1px 0 0 rgba(0,0,0,0.08), 6px 0 10px -6px rgba(12,10,9,0.12)"
                                : "none",
                              minWidth: 280,
                              maxWidth: 360,
                            }}
                          >
                            <Box>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                spacing={0.75}
                                sx={{ width: "100%" }}
                              >
                                {item.source === "email" &&
                                  item.email_body_file_id && (
                                    <Tooltip title={"View email"} arrow>
                                      <IconButton
                                        size={"small"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEmailDialogItem(item);
                                        }}
                                        sx={{ p: 0.25, flexShrink: 0 }}
                                      >
                                        <EmailIcon
                                          sx={{
                                            fontSize: "0.95rem",
                                            color: "primary.main",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                <Typography
                                  variant={"body2"}
                                  fontWeight={600}
                                  noWrap
                                  sx={{
                                    maxWidth: 320,
                                    fontSize: density.nameSize,
                                  }}
                                >
                                  {item.file_name}
                                </Typography>
                                {item.triage_reviewed && (
                                  <Tooltip title={"Reviewed"} arrow>
                                    <ReviewedIcon
                                      sx={{
                                        fontSize: "0.85rem",
                                        color: "success.main",
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {/* Row actions — kept in this pinned cell so they
                                    need no column of their own and stay reachable
                                    when the table is scrolled right. Space is
                                    always reserved so hovering never shifts the row. */}
                                <Box
                                  className={"row-actions"}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    ml: "auto",
                                    flexShrink: 0,
                                    display: "flex",
                                    opacity: 0,
                                    transition: "opacity 120ms",
                                  }}
                                >
                                  <RowMenu
                                    item={item}
                                    onViewReport={handleRowClick}
                                    onArchive={handleArchive}
                                    onMarkReviewed={handleMarkReviewed}
                                    onCreateSubmission={handleCreateSubmission}
                                  />
                                </Box>
                              </Stack>
                              <Typography
                                sx={{
                                  fontSize: density.metaSize,
                                  fontFamily: "monospace",
                                  color: "text.disabled",
                                  mt: density.metaGap,
                                  ml: "1.7rem",
                                }}
                              >
                                {formatDateShort(item.triage_completed_at)}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Other visible columns */}
                          {visibleColumns
                            .filter((c) => c.key !== "file")
                            .map((col) => (
                              <TableCell key={col.key} sx={{ maxWidth: 200 }}>
                                {renderCell(item, col.key, {})}
                              </TableCell>
                            ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Table footer */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant={"caption"} color={"text.secondary"}>
                {sortedRows.length > 0 ? (
                  <>
                    <Box
                      component={"span"}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {`${page * ROWS_PER_PAGE + 1} – ${Math.min(
                        (page + 1) * ROWS_PER_PAGE,
                        sortedRows.length
                      )}`}
                    </Box>
                    {` of ${sortedRows.length} submissions`}
                  </>
                ) : (
                  `0 of ${effectiveItems.length} submissions`
                )}
              </Typography>
              <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
                {/* Column counter pill */}
                <Typography
                  variant={"caption"}
                  color={"text.disabled"}
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    userSelect: "none",
                  }}
                >
                  {`\u2039 ${visibleColumns.length} of ${displayColumns.length} columns \u203a`}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} spacing={0.25}>
                  <IconButton
                    size={"small"}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    sx={{
                      width: 26,
                      height: 26,
                      opacity: page === 0 ? 0.3 : 1,
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    sx={{
                      minWidth: 44,
                      textAlign: "center",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {`${page + 1} / ${totalPages}`}
                  </Typography>
                  <IconButton
                    size={"small"}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    sx={{
                      width: 26,
                      height: 26,
                      opacity: page >= totalPages - 1 ? 0.3 : 1,
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </DashboardContent>

      {/* ── Side panel (Triage Report Drawer) ───────────────────────── */}
      <TriageResultDialog
        useDrawer={true}
        open={Boolean(viewReportItem)}
        onClose={() => setViewReportItem(null)}
        result={reportLoading ? null : reportData}
        fileId={viewReportItem?.fileId}
        folderId={viewReportItem?.folderId}
        fileName={viewReportItem?.fileName ?? ""}
        item={viewReportLiveItem}
        ruleset={viewReportRuleset}
        onArchive={handleArchive}
        onOpenRuleset={() => {
          setViewReportItem(null);
          setEditRulesetsOpen(true);
        }}
        onShowEmail={
          viewReportLiveItem?.source === "email" &&
          viewReportLiveItem?.email_body_file_id
            ? () => setEmailDialogItem(viewReportLiveItem)
            : null
        }
        onMarkReviewed={handleMarkReviewed}
        onDownloadFile={(f) =>
          downloadSource({ file_id: f.file_id, filename: f.file_name })
        }
        onRerunTriage={handleRerunTriage}
        onAddFiles={
          viewReportLiveItem
            ? (dropped) => {
                setAttachFilesDropped(dropped || null);
                setAttachFilesItem(viewReportLiveItem);
              }
            : null
        }
        onDetachFile={
          viewReportLiveItem
            ? (file) => handleDetachFile(viewReportLiveItem, file)
            : null
        }
      />

      {/* ── Attach files to an existing triage item ──────────────────── */}
      <AttachFilesDialog
        open={Boolean(attachFilesItem)}
        onClose={() => {
          setAttachFilesItem(null);
          setAttachFilesDropped(null);
        }}
        initialFiles={attachFilesDropped}
        item={attachFilesItem}
        onAttached={refetchTriageResult}
      />

      {/* ── Remove a document from a triage item ─────────────────────── */}
      <Dialog
        open={Boolean(detachTarget)}
        onClose={() => !isDetaching && setDetachTarget(null)}
        maxWidth={"xs"}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
          {"Remove this document?"}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Stack spacing={1.25}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={0.75}
              sx={(theme) => ({
                px: "10px",
                py: "6px",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.text.primary, 0.04),
              })}
            >
              <FileIcon sx={{ fontSize: "0.8rem", color: "text.disabled" }} />
              <Typography sx={{ fontSize: 12.5 }} noWrap>
                {detachTarget?.file?.file_name}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {
                "Every future triage run stops reading it. The report stays visible but is marked out of date until you re-run."
              }
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            size={"small"}
            color={"secondary"}
            sx={{ textTransform: "none" }}
            onClick={() => setDetachTarget(null)}
            isDisabled={isDetaching}
          >
            {"Cancel"}
          </Button>
          <Button
            size={"small"}
            color={"error"}
            sx={{ textTransform: "none" }}
            onClick={confirmDetach}
            isLoading={isDetaching}
            isDisabled={isDetaching}
          >
            {isDetaching ? "Removing…" : "Remove document"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Column picker modal ──────────────────────────────────────── */}
      <ColumnPickerModal
        open={colPickerOpen}
        onClose={() => setColPickerOpen(false)}
        columns={displayColumns}
        onApply={(newCols) => {
          setColumns(newCols);
          setColPickerOpen(false);
        }}
      />

      {/* ── Filter modal ─────────────────────────────────────────────── */}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onApply={setFilters}
        availableRulesets={availableRulesets}
        availableBrokers={availableBrokers}
      />

      {/* ── Ruleset editor modal ─────────────────────────────────────── */}
      <Dialog
        open={editRulesetsOpen}
        onClose={() => setEditRulesetsOpen(false)}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: { width: 860, maxWidth: "calc(100vw - 32px)", borderRadius: 2 },
          },
        }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2.5 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            {/* Left: title + count */}
            <Stack direction={"row"} alignItems={"center"} spacing={1.25}>
              <LayersIcon sx={{ fontSize: "1.2rem", color: "text.primary" }} />
              <Box>
                <Typography variant={"h6"} fontWeight={600} lineHeight={1.1}>
                  {"Triage Rulesets"}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {`${rulesetsCount} ruleset${rulesetsCount !== 1 ? "s" : ""}`}
                </Typography>
              </Box>
            </Stack>
            {/* Right: Add + Save + close */}
            <Stack direction={"row"} alignItems={"center"} spacing={1}>
              <MuiButton
                size={"small"}
                variant={"outlined"}
                startIcon={<AddIcon />}
                onClick={() => rulesetsRef.current?.addRuleset()}
                sx={{
                  fontSize: "0.78rem",
                  textTransform: "none",
                  color: "text.primary",
                  borderColor: "rgba(0,0,0,0.23)",
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                    borderColor: "rgba(0,0,0,0.4)",
                  },
                }}
              >
                {"Add ruleset"}
              </MuiButton>
              <Button
                size={"small"}
                color={"secondary"}
                sx={{ textTransform: "none" }}
                onClick={() => rulesetsRef.current?.save()}
              >
                {"Save"}
              </Button>
              <IconButton
                size={"small"}
                onClick={() => setEditRulesetsOpen(false)}
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon fontSize={"small"} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TriageRulesetsConfig
            ref={rulesetsRef}
            onCountChange={setRulesetsCount}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={0.75}
            sx={{ color: "text.secondary" }}
          >
            <InfoIcon sx={{ fontSize: "0.85rem" }} />
            <Typography variant={"caption"}>
              {"Changes apply to new submissions. Re-run to update existing."}
            </Typography>
          </Stack>
          <Stack direction={"row"} spacing={1}>
            <MuiButton
              size={"small"}
              variant={"outlined"}
              sx={{
                textTransform: "none",
                color: "text.primary",
                borderColor: "rgba(0,0,0,0.23)",
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "rgba(0,0,0,0.4)",
                },
              }}
              onClick={() => setEditRulesetsOpen(false)}
            >
              {"Cancel"}
            </MuiButton>
            <Button
              size={"small"}
              color={"secondary"}
              sx={{ textTransform: "none" }}
              onClick={async () => {
                await rulesetsRef.current?.save();
                setEditRulesetsOpen(false);
              }}
            >
              {"Save changes"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ── Email body dialog ────────────────────────────────────────── */}
      <EmailBodyDialog
        open={Boolean(emailDialogItem)}
        item={emailDialogItem}
        onClose={() => setEmailDialogItem(null)}
        onViewTriage={() => {
          const current = emailDialogItem;
          setEmailDialogItem(null);
          handleRowClick(current);
        }}
      />

      {/* ── AI carry-forward review page ─────────────────────────────── */}
      <SubmissionReviewDialog
        open={Boolean(carryForwardItem)}
        title={
          carryForwardItem?.ingestion_fields?.insured?.value ||
          carryForwardItem?.file_name
        }
        ingestionFields={carryForwardItem?.ingestion_fields}
        sections={TRIAGE_REVIEW_SECTIONS}
        showConfidence={showConfidence}
        editable={carryForwardEditEnabled}
        agentSearchConfig={reviewAgentSearchConfig}
        onAgentSearch={
          agentSearchFlagEnabled
            ? async (insuredName, fields, modes) =>
                agentSearchIngestionFields({
                  insuredName,
                  fields,
                  modes,
                  // Labels brief the agent on fields no rule describes.
                  descriptions: {
                    ...FIELD_LABELS,
                    ...carryForwardSourcing.descriptions,
                  },
                }).unwrap()
            : null
        }
        resolveEnum={resolveEnumForReview}
        onDownloadSource={downloadSource}
        onClose={() => setCarryForwardItem(null)}
        onConfirm={(
          fieldKeys,
          decisions,
          fieldOverrides,
          fieldSources,
          research
        ) => {
          const current = carryForwardItem;
          setCarryForwardItem(null);
          commitCreateSubmission(
            current,
            fieldKeys,
            decisions,
            fieldOverrides,
            fieldSources,
            research
          );
        }}
      />
    </Dashboard>
  );
};

export default TriagePipeline;
